import { makeStyles } from 'tss-react/mui'
import PageData from '@v4/utils/data/PageData'
import { isHtmlEmpty } from '@v4/hooks/useEditor/useEditor.utils'
import { ICON_UNICODE_MAP } from '@v4/components/common/FAIcons/FAIcons.const'
import { LIST_TYPES } from './EditableHTML.const'

const heading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
const excludedColors = ['button', 'inherit']

const useStyles = makeStyles()((theme, props) => {
	const isWhite = ({ white, pageType }, withShadow = false) => {
		if (withShadow && PageData.isTypeResultPdf(pageType)) {
			return false
		}
		return white === true || (white === 'theme' && theme.palette.whiteText)
	}

	const calcColorFromSx = (newProps, { breakpointValue, textColor }) => {
		const white = newProps.sx?.white?.[breakpointValue]

		if (white === undefined) return null

		return isWhite({ ...newProps, white }) ? '#FFFFFF' : textColor
	}

	function createBreakpoints(createStyles) {
		const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

		return _.reduce(
			breakpoints,
			(acc, breakpointValue) => {
				acc[theme.breakpoints.down(breakpointValue)] = createStyles(breakpointValue)

				return acc
			},
			{},
		)
	}
	function createHeadingCSS() {
		const styles = {}

		for (let i = 1; i <= 6; i += 1) {
			const headingTag = `h${i}`
			const key = `& ${headingTag}`

			styles[key] = {
				...theme.typography[headingTag],
				color: isWhite(props) ? '#FFFFFF' : theme.palette.getTextColors(props.usePaperColors).primary,
				marginBottom: theme.spacing(i <= 2 ? 2 : 1.5),

				...createBreakpoints(breakpointValue => ({
					color: calcColorFromSx(props, {
						breakpointValue,
						textColor: theme.palette.getTextColors(props.usePaperColors).primary,
					}),
				})),
			}
			if (!theme.isPdf && i < 4) {
				styles[key]['& b, & strong'] = {
					fontWeight: 700,
				}
			}
		}

		return styles
	}

	const getLinkColor = ({ usePaperColors }) =>
		theme.palette.calcContrastColor(
			theme.palette.primary.main,
			usePaperColors ? theme.palette.background.paper : theme.palette.background.default,
		)

	const listIconUnicode = ICON_UNICODE_MAP[props.listIcon] || ''

	const isBulletList = props.listIcon === LIST_TYPES.bullet
	const isNumberList = props.listIcon === LIST_TYPES.number
	const isNativeList = isBulletList || isNumberList

	const getListStyleType = () => {
		if (isBulletList) return 'disc'
		if (isNumberList) return 'decimal'
		return 'none'
	}

	const textShadowFrom = isWhite(props, true) || props.shadow ? `0 0 4px rgb(0 0 0 / ${props.textShadow}%)` : null

	const calcTextShadowFromSx = (newProps, breakpointValue) => {
		const white = newProps.sx?.white?.[breakpointValue]
		const shadow = newProps.sx?.shadow?.[breakpointValue]

		if (white === undefined && shadow === undefined) return null

		const isShadow = shadow ?? newProps.shadow

		return isWhite({ ...newProps, white: white ?? newProps.white }, true) || isShadow
			? `0 0 4px rgb(0 0 0 / ${newProps.textShadow}%)`
			: 'none'
	}

	const getRootColor = () => {
		if (isWhite(props)) return '#FFFFFF'
		if (excludedColors.includes(props.variant)) return undefined
		const colors = theme.palette.getTextColors(props.usePaperColors)

		return heading.includes(props.variant) ? colors.primary : colors.secondary
	}

	const getRootBreakPointColor = breakpointValue => {
		const white = props.sx?.white?.[breakpointValue]

		if (white === undefined) return null

		if (isWhite({ ...props, white })) return '#FFFFFF'

		if (excludedColors.includes(props.variant)) return undefined
		const colors = theme.palette.getTextColors(props.usePaperColors)

		return heading.includes(props.variant) ? colors.primary : colors.secondary
	}

	const getOutlinedColor = breakpointValue => {
		const white = props.sx?.white?.[breakpointValue]

		if (white === undefined) return null

		const color = isWhite({ ...props, white })
			? '#FFFFFF'
			: theme.palette.calcContrastColor(
					theme.palette.scoreapp.primary,
					props.usePaperColors ? theme.palette.background.paper : theme.palette.background.default,
			  )
		return `1px dashed ${color}`
	}

	return {
		root: {
			width: '100%',
			maxWidth: '100%',
			overflowWrap: 'break-word',
			textDecoration:
				(!_.includes(['body1', 'body2'], props.variant) && theme.typography.button.textDecoration) || 'none',
			color: getRootColor(),
			textShadow: textShadowFrom,
			...createBreakpoints(breakpointValue => ({
				color: getRootBreakPointColor(breakpointValue),
				textShadow: calcTextShadowFromSx(props, breakpointValue),
			})),

			'& p': {
				fontSize: ({ variant = 'body2' }) => theme.typography[variant]?.fontSize || null,
				'& .p-primary': {
					fontSize: theme.isPdf ? theme.typography.subtitle2.fontSize : null,
				},
			},
			'& a': {
				color: isWhite(props) ? '#FFFFFF' : getLinkColor(props),
				textShadow: textShadowFrom,
				'&:hover, &:focus, &:active': {
					color: isWhite(props) ? '#FFFFFF' : getLinkColor(props),
					textDecoration: 'none',

					...createBreakpoints(breakpointValue => ({
						color: calcColorFromSx(props, {
							breakpointValue,
							textColor: getLinkColor(props),
						}),
					})),
				},
				'&:focus': {
					outlineColor: isWhite(props) ? '#FFFFFF' : getLinkColor(props),

					...createBreakpoints(breakpointValue => ({
						outlineColor: calcColorFromSx(props, {
							breakpointValue,
							textColor: getLinkColor(props),
						}),
					})),
				},

				...createBreakpoints(breakpointValue => ({
					color: calcColorFromSx(props, {
						breakpointValue,
						textColor: getLinkColor(props),
					}),
					textShadow: calcTextShadowFromSx(props, breakpointValue),
				})),
			},
			'& ul': theme.isPdf
				? {}
				: {
						paddingLeft: isNativeList ? theme.spacing(3.666) : 0,
						listStyleType: getListStyleType(),
						'& li': {
							position: isNativeList ? null : 'relative',
							paddingLeft: isNativeList ? 0 : theme.spacing(3.666),
							paddingTop: theme.spacing(0.3),
							paddingBottom: theme.spacing(1),
						},
						'& li::before': {
							position: 'absolute',
							left: 0,
							content: isNativeList ? null : `"${listIconUnicode}"`,
							fontFamily: '"Font Awesome Kit"',
							fontWeight: 'normal',
							fontSize: theme.spacing(2.3),
							top: 0,
							color: isWhite(props)
								? '#FFFFFF'
								: theme.palette.common.listIconColor || theme.palette.primary.main,
							textShadow: textShadowFrom,

							...createBreakpoints(breakpointValue => ({
								color: calcColorFromSx(props, {
									breakpointValue,
									textColor: theme.palette.common.listIconColor || theme.palette.primary.main,
								}),
								textShadow: calcTextShadowFromSx(props, breakpointValue),
							})),
						},
				  },
			'& ol': {
				paddingLeft: theme.spacing(1.75),
			},
			'& ul, ol, p': {
				margin: '0 0 1em',
				textDecoration: ({ variant = 'body2' }) => theme.typography[variant]?.textDecoration || null,
			},
			'& > *:last-child': {
				marginBottom: 0,
			},
			'& b, & strong': {
				fontWeight: !theme.isPdf ? 500 : null,
			},
			...createHeadingCSS(),
			'& .p-primary': {
				color: isWhite(props) ? '#FFFFFF' : theme.palette.getTextColors(props.usePaperColors).primary,
				fontWeight: 500,

				...createBreakpoints(breakpointValue => ({
					color: calcColorFromSx(props, {
						breakpointValue,
						textColor: theme.palette.getTextColors(props.usePaperColors).primary,
					}),
				})),
			},
			'& img': {
				maxWidth: '100%',
			},
		},
		editable: {
			cursor: 'text',
			'&:focus': {
				minWidth: '80px',
			},
			'&:hover, &:focus-visible, &.mce-edit-focus': {
				outline: `1px dashed ${
					isWhite(props)
						? '#FFFFFF'
						: theme.palette.calcContrastColor(
								theme.palette.scoreapp.primary,
								props.usePaperColors
									? theme.palette.background.paper
									: theme.palette.background.default,
						  )
				}`,
				outlineOffset: -1,

				...createBreakpoints(breakpointValue => ({
					outline: getOutlinedColor(breakpointValue),
				})),
			},

			'&.MuiTypography-root[data-placeholder]': {
				position: 'relative',
				minWidth: isHtmlEmpty(props.html) ? '80px' : '0',
				'&::before': {
					content: isHtmlEmpty(props.html) ? 'attr(data-placeholder)' : '""',
					opacity: 1,
					color: isWhite(props)
						? '#FFFFFF'
						: theme.palette.calcContrastColor(
								theme.palette.scoreapp.placeholder,
								props.usePaperColors
									? theme.palette.background.paper
									: theme.palette.background.default,
						  ),
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 0,
					font: 'inherit',
					lineHeight: 'inherit',
					transition: 'opacity 0.1s',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					direction: theme.direction || 'ltr',

					...createBreakpoints(breakpointValue => ({
						color: calcColorFromSx(props, {
							breakpointValue,
							textColor: theme.palette.calcContrastColor(
								theme.palette.scoreapp.placeholder,
								props.usePaperColors
									? theme.palette.background.paper
									: theme.palette.background.default,
							),
						}),
					})),
				},
			},
		},
	}
})

export default useStyles
